import React, { useCallback, useMemo, useState } from "react";

import { Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getJobName } from "Util/getJobName";

import FieldView from "../../../../../../../components/Form/FieldView";
import AddressComponent from "../../../../../../../components/Generic/AddressComponent";
import GeneralCommentsComponent from "../../../../../../../components/Generic/GeneralComments";
import { useAppState } from "../../../../../../../context/StateContext";
import { Branch } from "../../../../../../../models/branch";
import { Order, PreApprovalItem } from "../../../../../../../models/order";
import { CustomerLocations } from "../../../../../../../models/sales";
import BranchService from "../../../../../../../services/branch.service";
import { calcTotalItems } from "../../../../../../../util/calcTotalItems";
import { handleGetConvertValue } from "../../../../../../../util/converts";
import { Status } from "../../../../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import { getTotalItemsValuesByInventory } from "../../../../../../../util/inventory";
import useStyles from "./styles";

interface Props {
  order?: Order;
}

type LegacyPreApprovalFields = {
  preApprovalsGoShareId?: string;
  preApprovalsCode?: string;
  preApprovalsDescription?: string;
  preApprovalsCount?: number | string | null;
  preApprovalsUnit?: string;
  preApprovalsCurrency?: string;
  preApprovalsRate?: number | string | null;
  preApprovalsTotal?: number | string | null;
};

export default function FormOnViewMode(props: Props) {
  const { order } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const [branches, setBranches] = useState<Branch[]>([]);

  const measurement = getMeasurement();
  const { code: measurementCode, decimalPlaces } = measurement;
  const measurementWeight = getWeightMeasurement();

  useQuery(
    ["branches.list"],
    () =>
      BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "ASC"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!order?.id,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setBranches(response.data);
      },
    }
  );

  const consultantName = useMemo(() => {
    if (order?.user) {
      const name = order?.user?.name;
      const lastName = order?.user?.lastName;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return undefined;
  }, [order?.user]);

  const managerName = useMemo(() => {
    if (order?.manager) {
      const name = order?.manager?.name;
      const lastName = order?.manager?.lastName;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return undefined;
  }, [order?.manager]);

  const moveCoordinatorName = useMemo(() => {
    if (order?.moveCoordinator) {
      const name = order?.moveCoordinator?.name;
      const lastName = order?.moveCoordinator?.lastName;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return undefined;
  }, [order]);

  const branchName = useCallback(
    (id?: number) => {
      if (!id) return undefined;

      const branch = branches?.find((bran) => Number(bran.id) === Number(id));

      if (branch) {
        const { name } = branch;

        return name;
      }

      return undefined;
    },
    [order, branches]
  );

  const getInventoryData = useCallback(() => {
    return getTotalItemsValuesByInventory(order?.inventory, getMeasurement());
  }, [order, order?.inventory]);

  const { totalVolume, totalQuantity } = useMemo(
    () =>
      calcTotalItems({
        items: order?.actualInventory,
        weightMeasurement: measurementWeight,
        measurement,
      }),
    [order?.actualInventory, measurementWeight, measurement]
  );

  const jobSizeConfirmationText = useMemo(() => {
    if (!order?.sizeConfirmation) return "";

    let label = `${totalQuantity} item(s) - `;

    label += `${handleGetConvertValue(
      Number(order.sizeConfirmation),
      measurementCode,
      decimalPlaces
    )} ${measurementCode}`;

    return label;
  }, [order, totalQuantity]);

  const quotedQuantity = useMemo(() => {
    return order?.quotedQuantity ?? order?.customQuantityQuoted ?? getInventoryData().quantity;
  }, [order?.customQuantityQuoted, order?.customQuantityQuoted, getInventoryData]);

  const quotedVolume = useMemo(() => {
    if (
      (order?.quotedVolume !== undefined && order?.quotedVolume !== null) ||
      (order?.customVolumeQuoted !== undefined && order?.customVolumeQuoted !== null)
    ) {
      return handleGetConvertValue(
        order?.quotedVolume ?? (order?.customVolumeQuoted || 0),
        measurementCode,
        decimalPlaces
      ).toString();
    }

    return getInventoryData().volumeFormatted;
  }, [order?.quotedVolume, order?.customVolumeQuoted, getInventoryData]);

  const isMilitary = useMemo(
    () => Boolean(order?.company?.isMilitary ?? currentUser?.company?.isMilitary),
    [order?.company?.isMilitary, currentUser?.company?.isMilitary]
  );

  const formatOptionalField = (v: unknown) =>
    v !== undefined && v !== null && v !== "" ? String(v) : "";

  const preApprovalsForView = useMemo((): PreApprovalItem[] => {
    if (order?.preApprovals?.length) return order.preApprovals;
    const o = order as (Order & LegacyPreApprovalFields) | undefined;
    if (
      o?.preApprovalsGoShareId ||
      o?.preApprovalsCode ||
      o?.preApprovalsDescription ||
      o?.preApprovalsCount ||
      o?.preApprovalsUnit ||
      o?.preApprovalsCurrency ||
      o?.preApprovalsRate ||
      o?.preApprovalsTotal
    ) {
      return [
        {
          goShareId: o.preApprovalsGoShareId,
          code: o.preApprovalsCode,
          description: o.preApprovalsDescription,
          count: o.preApprovalsCount,
          unit: o.preApprovalsUnit,
          currency: o.preApprovalsCurrency,
          rate: o.preApprovalsRate,
          total: o.preApprovalsTotal,
        },
      ];
    }
    return [];
  }, [order]);

  return (
    <div className={classes.wrapper} key={order?.id}>
      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView
            label={translate("orderType")}
            value={order?.jobType?.name || order?.jobType}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView label={translate("reference")} value={getJobName(order)} />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("secondReference")} value={order?.secondReference} />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView label={translate("status")} value={order?.jobStatus} />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView label={translate("consultant")} value={consultantName} />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3} className={classes.p08}>
          <FieldView label={translate("code")} value={order?.user?.code} />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView label={translate("moveCoordinator")} value={moveCoordinatorName} />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView label={translate("manager")} value={managerName} />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("inventoryLeader")} value={`${jobSizeConfirmationText}`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView
            label={translate("inventoryActual")}
            value={`${
              order?.customQuantityActual === null || order?.customQuantityActual === undefined
                ? totalQuantity
                : order?.customQuantityActual
            } item(s) - ${
              order?.customVolumeActual === null || order?.customVolumeActual === undefined
                ? totalVolume
                : handleGetConvertValue(
                    order?.customVolumeActual || 0,
                    measurementCode,
                    decimalPlaces
                  ).toString()
            } ${measurementCode}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView
            label={translate("inventoryQuoted")}
            value={`${quotedQuantity} item(s) - ${quotedVolume} ${measurementCode}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("mode")} value={order?.mode ? translate(order.mode.toLowerCase()) : ""} />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView
            label={translate("tags")}
            value={order?.tag ? order?.tag?.split(",") : undefined}
            valueClass={classes.tag}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("accountType")} value={order?.accountType} />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("transportationType")} value={order?.transportationType} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FieldView label={translate("vipTreatment")} value={order?.vipTreatment} />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" spacing={2}>
        {!isMilitary && (
          <Grid item xs={12} sm={6} lg={3}>
            <FieldView
              label={translate("scac").toUpperCase()}
              value={order?.carrier ? `${order?.carrier?.scac}-${order?.carrier?.name}` : ""}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView
            label={translate("branchOrigin")}
            value={branchName(order?.branchFromId) || ""}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <FieldView
            label={translate("branchDestination")}
            value={branchName(order?.toBranchId) || ""}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <FieldView
            label={translate("leadSourceFrom")}
            value={String(order?.leadSourceFrom || "")}
          />
        </Grid>
      </Grid>

      {isMilitary && (
        <>
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <FieldView label={translate("grade")} value={order?.grade ?? ""} />
            </Grid>
          </Grid>

          <Grid container direction="column" >
            <Grid xs={12}>
              <Typography 
                className={classes.militarySectionTitle}
                component="div"
              >
                {translate("scacInformation")}
              </Typography>
            </Grid>
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("scacCode")} value={order?.scacCode ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView
                  label={translate("scacDescription")}
                  value={order?.scacDescription ?? ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("phone")} value={order?.scacPhone ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("email")} value={order?.scacEmail ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("claimsPhone")} value={order?.scacClaimsPhone ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("claimsEmail")} value={order?.scacClaimsEmail ?? ""} />
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column">
            <Grid xs={12}>
              <Typography 
                className={classes.militarySectionTitle}
                component="div"
              >
                {translate("storageInTransitOriginSit")}
              </Typography>
            </Grid>
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("sitInDate")} value={order?.sitInDate ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("sitOutDate")} value={order?.sitOutDate ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label={translate("sitNumber")} value={order?.sitNumber ?? ""} />
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column">
            <Grid xs={12}>
              <Typography 
                className={classes.militarySectionTitle}
                component="div"
              >
                {translate("weightAndMeasurements")}
              </Typography>
            </Grid>
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FieldView label={translate("grossWeight")} value={order?.grossWeight ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FieldView label={translate("tareWeight")} value={order?.tareWeight ?? ""} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FieldView label={translate("netWeight")} value={order?.netWeight ?? ""} />
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column">
            <Grid xs={12}>
              <Typography
                className={classes.militarySectionTitle}
                component="div"
              >
                {translate("preApprovals")}
              </Typography>
            </Grid>
            {preApprovalsForView.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" component="span">
                  {translate("noPreApprovals")}
                </Typography>
              </Grid>
            )}
            {preApprovalsForView.length > 0 && (
              <Grid item xs={12}>
                <div className={classes.preApprovalTableContainer}>
                  <div className={classes.preApprovalTable}>
                    <div className={`${classes.preApprovalGridRowView} ${classes.preApprovalHeadRow}`}>
                      <div className={classes.preApprovalHeadCell}>{translate("code")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("description")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("count")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("unit")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("currency")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("rate")}</div>
                      <div className={classes.preApprovalHeadCell}>{translate("total")}</div>
                    </div>
                    {preApprovalsForView.map((item: PreApprovalItem, index: number) => (
                      <div
                        key={item.id ?? `pre-approval-view-${index}`}
                        className={`${classes.preApprovalGridRowView} ${
                          preApprovalsForView.length === 1
                            ? classes.preApprovalBodyRowPlain
                            : index % 2 === 0
                              ? classes.preApprovalBodyRowZebra
                              : classes.preApprovalBodyRowPlain
                        }`}
                      >
                        <div className={classes.preApprovalBodyCell}>{item.code ?? ""}</div>
                        <div className={classes.preApprovalBodyCell}>{item.description ?? ""}</div>
                        <div className={classes.preApprovalBodyCell}>
                          {formatOptionalField(item.count)}
                        </div>
                        <div className={classes.preApprovalBodyCell}>{item.unit ?? ""}</div>
                        <div className={classes.preApprovalBodyCell}>{item.currency ?? ""}</div>
                        <div className={classes.preApprovalBodyCell}>
                          {formatOptionalField(item.rate)}
                        </div>
                        <div className={classes.preApprovalBodyCell}>
                          {formatOptionalField(item.total)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item sm={12} md={12} lg={12}>
          <AddressComponent
            isOnViewMode
            isRenderOnViewComponent
            title={translate("pickup")}
            access={order?.pickupAccess}
            inventory={order?.inventory || []}
            customerLocations={order?.origin || ({} as CustomerLocations)}
            {...(isMilitary
              ? {
                  showMilitaryLocationExtensionInView: true,
                  locationExtension: {
                    baseCode: order?.pickupBaseCode,
                    baseDescription: order?.pickupBaseDescription,
                    areaCode: order?.pickupAreaCode,
                    areaDescription: order?.pickupAreaDescription,
                    agentName: order?.pickupAgentName,
                    agentZip: order?.pickupAgentZip,
                    agentCity: order?.pickupAgentCity,
                  },
                }
              : {})}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={12}>
          <AddressComponent
            isOnViewMode
            isRenderOnViewComponent
            title={translate("delivery")}
            access={order?.deliveryAccess}
            inventory={order?.inventory || []}
            customerLocations={order?.destination || ({} as CustomerLocations)}
            {...(isMilitary
              ? {
                  showMilitaryLocationExtensionInView: true,
                  locationExtension: {
                    baseCode: order?.deliveryBaseCode,
                    baseDescription: order?.deliveryBaseDescription,
                    areaCode: order?.deliveryAreaCode,
                    areaDescription: order?.deliveryAreaDescription,
                    agentName: order?.deliveryAgentName,
                    agentZip: order?.deliveryAgentZip,
                    agentCity: order?.deliveryAgentCity,
                  },
                }
              : {})}
          />
        </Grid>
        {order?.quoteComments?.length ? (
          <Grid item sm={12} md={12} lg={12}>
            <GeneralCommentsComponent comments={order?.quoteComments} isViewMode />
          </Grid>
        ) : null}
      </Grid>
    </div>
  );
}
